/**
 * TableDiffView — the preview for a table-aware "edit highlight" proposal.
 *
 * Renders the highlighted rows as a compact table with the CHANGED cells
 * visually highlighted (soft tint + the new value; the prior value shown
 * beneath as "was …"). Rows the rewrite couldn't parse are marked as
 * left unchanged. Pure presentation — the diff model is built upstream by
 * services/tableEdit (buildSnippet); Approve applies it via
 * useHighlightEditFlow. Rendered inside ProposalCard via ``previewNode``.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import type { TableEditSnippet } from "../services/tableEdit";
import { brandColors, fonts, inkColors, statusColors } from "../tokens";

interface TableDiffViewProps {
  snippet: TableEditSnippet;
}

const useStyles = makeStyles({
  scroll: {
    overflowX: "auto",
    maxWidth: "100%",
  },
  table: {
    borderCollapse: "collapse",
    fontSize: "11.5px",
    fontFamily: fonts.body,
    color: inkColors.ink2,
    width: "100%",
  },
  th: {
    textAlign: "left",
    fontWeight: 600,
    fontSize: "10px",
    letterSpacing: "0.2px",
    color: inkColors.ink3,
    borderBottom: `1px solid ${brandColors.glow}`,
    padding: "3px 8px",
    whiteSpace: "nowrap",
  },
  td: {
    verticalAlign: "top",
    borderBottom: `1px solid ${brandColors.glow}`,
    padding: "3px 8px",
  },
  cellChanged: {
    backgroundColor: "#E9FBF3",
    boxShadow: `inset 2px 0 0 ${statusColors.cool}`,
    fontWeight: 600,
    color: inkColors.ink,
  },
  wasLine: {
    display: "block",
    marginTop: "2px",
    fontSize: "9.5px",
    fontWeight: 400,
    color: inkColors.ink4,
    textDecoration: "line-through",
  },
  skippedBadge: {
    display: "inline-block",
    marginLeft: "6px",
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: statusColors.warn,
  },
  legend: {
    marginTop: "6px",
    fontSize: "10px",
    color: inkColors.ink4,
  },
  swatch: {
    display: "inline-block",
    width: "9px",
    height: "9px",
    borderRadius: "2px",
    backgroundColor: "#E9FBF3",
    boxShadow: `inset 2px 0 0 ${statusColors.cool}`,
    marginRight: "4px",
    verticalAlign: "middle",
  },
});

export const TableDiffView: React.FC<TableDiffViewProps> = ({ snippet }) => {
  const styles = useStyles();
  const colCount = snippet.header?.length ?? snippet.rows[0]?.cells.length ?? 0;

  return (
    <div>
      <div className={styles.scroll}>
        <table className={styles.table}>
          {snippet.header ? (
            <thead>
              <tr>
                {snippet.header.map((h, i) => (
                  // Header cells are fixed columns — index key is stable.
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed column order
                  <th key={i} className={styles.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {snippet.rows.map((row) => (
              <tr key={row.rowIndex}>
                {row.cells.map((cell, ci) => (
                  <td
                    // Columns are positional within a row — index key is stable.
                    // biome-ignore lint/suspicious/noArrayIndexKey: fixed column order
                    key={ci}
                    className={mergeClasses(styles.td, cell.changed && styles.cellChanged)}
                  >
                    {cell.changed ? cell.new : cell.old}
                    {cell.changed && cell.old.trim() ? <span className={styles.wasLine}>{cell.old}</span> : null}
                    {ci === 0 && row.skippedReason ? (
                      <span className={styles.skippedBadge} title={row.skippedReason}>
                        left unchanged
                      </span>
                    ) : null}
                  </td>
                ))}
                {/* Pad short skipped/edge rows so the grid stays rectangular. */}
                {row.cells.length < colCount
                  ? Array.from({ length: colCount - row.cells.length }).map((_, pi) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: padding cells
                      <td key={`pad-${pi}`} className={styles.td} />
                    ))
                  : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.legend}>
        <span className={styles.swatch} aria-hidden />
        {snippet.changedCells} cell{snippet.changedCells === 1 ? "" : "s"} changed across{" "}
        {snippet.rows.filter((r) => !r.skippedReason && r.cells.some((c) => c.changed)).length} row
        {snippet.rows.filter((r) => !r.skippedReason && r.cells.some((c) => c.changed)).length === 1 ? "" : "s"}
        {snippet.skippedRows > 0 ? ` · ${snippet.skippedRows} left unchanged` : ""}
      </div>
    </div>
  );
};

export default TableDiffView;
